import type { TimelineEvent } from "@/api/docParsing.service.js";
import styles from "./ParsingJobs.module.css";

type TimingBreakdownProps = {
  timeline: TimelineEvent[] | null;
  createdAt: string;
  status: string;
  retryCount: number;
};

const STEP_LABELS: Record<string, string> = {
  processing: "Processing",
  download: "SharePoint Download",
  extract: "Extract",
  parsed: "Parsed",
  db_save: "DB Save",
};

function formatDelta(ms: number): string {
  if (ms < 0) return "+0ms";
  if (ms >= 10_000) return `+${(ms / 1000).toFixed(1)}s`;
  return `+${Math.round(ms)}ms`;
}

function formatTime(timestamp: string): string {
  const d = new Date(timestamp);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  });
}

function getStatusClass(status: TimelineEvent["status"]): string {
  switch (status) {
    case "success":
      return `${styles.statusDot} ${styles.statusParsed}`;
    case "error":
      return `${styles.statusDot} ${styles.statusFailed}`;
    case "skipped":
      return `${styles.statusDot} ${styles.statusWaiting}`;
  }
}

function TimingBreakdown({ timeline, createdAt }: TimingBreakdownProps) {
  if (!timeline || timeline.length === 0) {
    return null;
  }

  let previousTimestamp = createdAt;
  let isFirstProcessing = true;

  const rows = timeline.map((event, index) => {
    const currentMs = new Date(event.timestamp).getTime();
    const prevMs = new Date(previousTimestamp).getTime();
    const delta = currentMs - prevMs;

    const isNewAttempt = event.step === "processing" && !isFirstProcessing;
    if (event.step === "processing") {
      isFirstProcessing = false;
    }

    previousTimestamp = event.timestamp;

    const stepLabel = STEP_LABELS[event.step] ?? event.step;
    const rowClass = isNewAttempt
      ? `${styles.timelineEvent} ${styles.timelineEventNew}`
      : styles.timelineEvent;

    return (
      <div key={index} className={rowClass}>
        <div className={styles.timelineMainRow}>
          <span className={getStatusClass(event.status)} />
          <span className={styles.timelineStep}>{stepLabel}</span>
          <span className={styles.timelineDelta}>{formatDelta(delta)}</span>
        </div>
        {event.details != null && (
          <div className={styles.timelineSecondRow}>
            <span className={styles.timelineDetails}>{event.details}</span>
          </div>
        )}
        <div className={styles.timelineSecondRow}>
          <span className={styles.timelineTimestamp}>{formatTime(event.timestamp)}</span>
        </div>
      </div>
    );
  });

  return (
    <div className={styles.timingCard}>
      <h3 className={styles.timingTitle}>Processing Timeline</h3>
      <div className={styles.timelineList}>{rows}</div>
    </div>
  );
}

export default TimingBreakdown;
